// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Huntress — the board, as real DOM buttons in a CSS grid.
//
// THE RULE THIS FILE IS BUILT AROUND: no layout geometry is ever measured in JavaScript. Cell
// positions come from the grid, cell size from the `--board` min() chain, and a token sits in
// normal flow inside its own cell. That is what makes a backgrounded peer's board correct — a tab
// whose clientWidth is 0 has nothing to get wrong — and it means every tap is a button's own click
// handler rather than a rect hit-test computed inside a frame that may never have run.
//
// The particle canvas is the one exception and is deliberately fixed-size (never height:100%, which
// collapses to the backing-store aspect ratio inside a flex parent) with everything authored in
// 0..1 space.

import { BURNT, EMPTY, cellName, sideOf, xOf, yOf, type State } from './rules';
import { SEAT_NAME } from './palette';

export interface LastMove {
  from: number;
  to: number;
  arrow: number;
  side: number;
}

export interface ViewHints {
  /** The square of the huntress currently picked up. */
  picked: number | null;
  /** Where she is provisionally standing, mid-turn. */
  pendingAt: number | null;
  /** Squares the CURRENT stage is asking for, and which stage that is. */
  targets: Set<number>;
  stage: 'idle' | 'picked' | 'moved';
  last: LastMove | null;
  /** The losing side's huntresses at the moment they were sealed in. */
  trapped: Set<number>;
  interactive: boolean;
  mySide: number | null;
}

export const emptyHints = (): ViewHints => ({
  picked: null,
  pendingAt: null,
  targets: new Set(),
  stage: 'idle',
  last: null,
  trapped: new Set(),
  interactive: false,
  mySide: null,
});

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  hue: string;
}

const FX = 1040;

export class BoardView {
  readonly root: HTMLElement;
  private cells: HTMLButtonElement[] = [];
  private fx: HTMLCanvasElement | null = null;
  private parts: Particle[] = [];
  private raf = 0;
  private freshBurn = -1;
  private w = 6;
  private h = 6;
  private dead = false;

  constructor(
    host: HTMLElement,
    s: State,
    private readonly onTap: (cell: number) => void,
    private readonly reducedMotion: boolean,
  ) {
    this.w = s.w;
    this.h = s.h;
    this.root = document.createElement('div');
    this.root.className = 'board';
    this.root.style.setProperty('--n', String(s.w));
    this.root.setAttribute('role', 'grid');
    this.root.setAttribute('aria-label', `${s.w} by ${s.h} board`);

    for (let i = 0; i < s.cells; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cell';
      b.dataset.i = String(i);
      // The checker is orientation only — it carries no information, which is why the two squares
      // are near-isoluminant (see palette.ts).
      if ((xOf(s, i) + yOf(s, i)) % 2 === 1) b.classList.add('dark');
      b.addEventListener('click', () => this.onTap(i));
      this.cells.push(b);
      this.root.appendChild(b);
    }
    host.appendChild(this.root);

    if (!reducedMotion) {
      this.fx = document.createElement('canvas');
      this.fx.className = 'fx';
      this.fx.width = FX;
      this.fx.height = FX;
      host.appendChild(this.fx);
      this.tick();
    }
  }

  markFreshBurn(cell: number): void {
    this.freshBurn = cell;
  }

  /** Paint the whole board. 36–64 cells, once a turn — a diff would be more code than work. */
  paint(s: State, v: ViewHints): void {
    if (this.dead) return;

    // A provisional move is drawn where the player put it, not where the state says.
    const occupied = new Map<number, number>();
    for (let hu = 0; hu < s.pos.length; hu++) {
      const at = s.pos[hu];
      if (at >= 0) occupied.set(at, hu);
    }
    let ghostFrom = -1;
    if (v.pendingAt !== null && v.picked !== null) {
      const hu = occupied.get(v.picked);
      if (hu !== undefined) {
        occupied.delete(v.picked);
        occupied.set(v.pendingAt, hu);
        ghostFrom = v.picked;
      }
    }

    for (let i = 0; i < this.cells.length; i++) {
      const el = this.cells[i];
      const burnt = s.cell[i] === BURNT;
      el.classList.toggle('burnt', burnt);
      el.classList.toggle('fresh', burnt && i === this.freshBurn);
      el.classList.toggle('t-step', v.stage === 'picked' && v.targets.has(i));
      el.classList.toggle('t-shot', v.stage === 'moved' && v.targets.has(i));
      el.classList.toggle('trail-from', v.last?.from === i);
      el.classList.toggle('trail-to', v.last?.arrow === i);
      el.classList.toggle('trapped', v.trapped.has(i));

      const hu = occupied.get(i);
      let tok = el.querySelector<HTMLElement>('.tok');
      if (hu === undefined) {
        tok?.remove();
      } else {
        const side = sideOf(s, hu);
        const label = String.fromCharCode(65 + (hu % s.k));
        if (!tok) {
          tok = document.createElement('i');
          tok.className = 'tok';
          tok.innerHTML = '<span></span>';
          el.appendChild(tok);
        }
        tok.className = `tok p${side + 1}`;
        tok.classList.toggle('picked', v.picked === i && v.pendingAt === null);
        tok.classList.toggle('ghost', i === v.pendingAt);
        const span = tok.querySelector('span');
        if (span) span.textContent = label;
      }
      if (ghostFrom === i) el.classList.add('vacated');
      else el.classList.remove('vacated');

      const what = burnt ? 'burnt' : hu === undefined ? 'empty' : `${SEAT_NAME[sideOf(s, hu)]} huntress ${String.fromCharCode(65 + (hu % s.k))}`;
      const role =
        v.stage === 'picked' && v.targets.has(i)
          ? ', can run here'
          : v.stage === 'moved' && v.targets.has(i)
            ? ', can burn this'
            : '';
      el.setAttribute('aria-label', `${cellName(s, i)}, ${what}${role}`);
      el.disabled = !v.interactive;
    }
  }

  /** Burst at a cell, in normalised board space so nothing is ever measured. */
  burst(cell: number, colour: string, n = 14): void {
    if (!this.fx || this.reducedMotion) return;
    const x = ((cell % this.w) + 0.5) / this.w;
    const y = (((cell / this.w) | 0) + 0.5) / this.h;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.random();
      const sp = 0.004 + Math.random() * 0.011;
      this.parts.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 0.004, life: 1, hue: colour });
    }
  }

  /** The arrow's flight: a line of sparks from where she stands to where it lands. */
  arrow(from: number, to: number, colour: string): void {
    if (!this.fx || this.reducedMotion) return;
    const ax = ((from % this.w) + 0.5) / this.w;
    const ay = (((from / this.w) | 0) + 0.5) / this.h;
    const bx = ((to % this.w) + 0.5) / this.w;
    const by = (((to / this.w) | 0) + 0.5) / this.h;
    const steps = 10;
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      this.parts.push({
        x: ax + (bx - ax) * t,
        y: ay + (by - ay) * t,
        vx: (bx - ax) * 0.012,
        vy: (by - ay) * 0.012,
        life: 0.35 + t * 0.5,
        hue: colour,
      });
    }
  }

  shake(px = 3): void {
    if (this.reducedMotion) return;
    this.root.style.setProperty('--shake', `${px}px`);
    this.root.classList.remove('shaking');
    void this.root.offsetWidth;
    this.root.classList.add('shaking');
  }

  nudge(cell: number): void {
    const el = this.cells[cell];
    if (!el) return;
    el.classList.remove('nope');
    void el.offsetWidth;
    el.classList.add('nope');
  }

  private tick = (): void => {
    if (this.dead || !this.fx) return;
    const ctx = this.fx.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, FX, FX);
      for (const p of this.parts) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.0005;
        p.life -= 0.026;
      }
      this.parts = this.parts.filter((p) => p.life > 0);
      for (const p of this.parts) {
        ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
        ctx.fillStyle = p.hue;
        const r = 4 + p.life * 8;
        ctx.beginPath();
        ctx.arc(p.x * FX, p.y * FX, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }
    this.raf = requestAnimationFrame(this.tick);
  };

  destroy(): void {
    this.dead = true;
    cancelAnimationFrame(this.raf);
    this.root.remove();
    this.fx?.remove();
  }
}

/**
 * The final board, with the ground shaded by who owned it — drawn on the results screen, where hit
 * testing is irrelevant. This is the "what you were actually playing for" picture: principle #9
 * asks for what everyone missed, and in this game that is the map of ground nobody got to.
 */
export function renderFinal(canvas: HTMLCanvasElement, s: State, map: Int8Array, colours: string[], palette: Record<string, string>): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const W = canvas.width;
  const H = canvas.height;
  const cs = Math.min(W / s.w, H / s.h);
  const ox = (W - cs * s.w) / 2;
  const oy = (H - cs * s.h) / 2;
  ctx.clearRect(0, 0, W, H);

  for (let i = 0; i < s.cells; i++) {
    const x = ox + xOf(s, i) * cs;
    const y = oy + yOf(s, i) * cs;
    const v = s.cell[i];
    ctx.fillStyle = v === BURNT ? palette.ash : (xOf(s, i) + yOf(s, i)) % 2 === 1 ? palette.shade : palette.glade;
    ctx.fillRect(x, y, cs, cs);

    if (v === EMPTY && map[i] >= 0) {
      ctx.globalAlpha = 0.42;
      ctx.fillStyle = colours[map[i]];
      ctx.fillRect(x, y, cs, cs);
      ctx.globalAlpha = 1;
    }
    if (v >= 0) {
      const side = sideOf(s, v);
      ctx.fillStyle = colours[side];
      ctx.strokeStyle = palette.ring;
      ctx.lineWidth = Math.max(1, cs * 0.06);
      ctx.beginPath();
      if (side === 0) {
        ctx.arc(x + cs / 2, y + cs / 2, cs * 0.32, 0, Math.PI * 2);
      } else {
        ctx.moveTo(x + cs / 2, y + cs * 0.16);
        ctx.lineTo(x + cs * 0.84, y + cs / 2);
        ctx.lineTo(x + cs / 2, y + cs * 0.84);
        ctx.lineTo(x + cs * 0.16, y + cs / 2);
        ctx.closePath();
      }
      ctx.fill();
      ctx.stroke();
    }
  }
  ctx.strokeStyle = palette.edge;
  ctx.lineWidth = 1;
  for (let i = 0; i <= s.w; i++) {
    ctx.beginPath();
    ctx.moveTo(ox + i * cs, oy);
    ctx.lineTo(ox + i * cs, oy + cs * s.h);
    ctx.stroke();
  }
  for (let i = 0; i <= s.h; i++) {
    ctx.beginPath();
    ctx.moveTo(ox, oy + i * cs);
    ctx.lineTo(ox + cs * s.w, oy + i * cs);
    ctx.stroke();
  }
}
